<template>

<div class="list-view">

	<div :class="showDetail ? 'sub-header detail-sub-header' : 'sub-header'">
		<div v-if="!showDetail" class="input-with-control">
			<input type="search" ref="search_term" v-model="filters.search_term" placeholder="Search..." @search="getList(1)" v-focus>
			<div class="button action input-go" @click="getList(1)"><i class="fas fa-search"></i></div>
		</div>
		<div v-if="showDetail" class="detail-title">{{ $fullName(listDetail) }}</div>
		<div v-if="!showDetail" class="controls">
			<pagination :paging="paging" @updatePage="goToPage" />
		</div>
		<pagination-detail v-if="showDetail && list.length > 0" :list="list" :index="listIndex" @updatePage="getItem" />
		<div v-if="showDetail && listDetail.contact_id != 'new'" class="detail-title-meta"><label>Contact ID: </label>{{ listDetail.contact_id }}</div>
	</div>

	<div class="filters" v-if="showFilters">

		<div v-if="!showDetail">
			<label>Contact's State</label>
			<state-select type="text" :selectedState="filters.state" :showProvinces="true" placeHolder="Select..." @updateSelected="updateStateFilter"/>

			<ul class="checkbox-group">
				<li>
					<span :class="['pointer', 'far', filters.store ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('store')"></span>
					<label>POS Customers</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.layaway ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('layaway')"></span>
					<label>Layaway Customers</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.web ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('web')"></span>
					<label>Web Customers</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.orderless ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('orderless')"></span>
					<label>Never Ordered</label>
				</li>
			</ul>

			<div class="divider"></div>

			<ul class="checkbox-group">
				<li>
					<span :class="['pointer', 'far', filters.inactive ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('inactive')"></span>
					<label>Deactivated</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.incomplete ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('incomplete')"></span>
					<label>Include Incomplete</label>
				</li>
			</ul>
			<div class="note">By default the list excludes records with incomplete contact info.</div>

			<div :class="['button', hasFilters ? 'neutral' : 'disabled']" @click="hasFilters ? clearFilters() : null">Clear Filters</div>

		</div>

		<div v-if="!showDetail">
			<div class="divider"></div>
			<div class="button action" @click="addItem()">Add Contact</div>
			<div class="divider"></div>
			<div :class="['extra-menu-item', { disabled: gettingExport }]" @click="doExport()">Export CSV</div>
			<div class="message">{{ exportMessage }}</div>
		</div>

		<div v-if="showDetail">
			<div :class="['filter-list-button-large', { active : showDetailTab == 'contact' }]" @click="fetchDetailTab('contact')">Contact Detail</div>
			<div :class="['filter-list-button-large', { active : showDetailTab == 'addresses' }]" @click="fetchDetailTab('addresses')">Addresses</div>
			<div :class="['filter-list-button-large', { active : showDetailTab == 'attachments' }]" @click="fetchDetailTab('attachments')">Waiver / Attachments</div>
			<div :class="['filter-list-button-large', { active : showDetailTab == 'user' }]" @click="fetchDetailTab('user')">User Account</div>
			<button :class="['button', canSave ? 'action' : 'disabled']" @click="saveItem()">Save {{ showDetailTab }}</button>
		</div>

		<div v-if="showDetail">
			<div class="divider"></div>
			<button class="button neutral" @click="backToList()">Back To List</button>
		</div>

		<div v-if="showDetail">
			<div class="divider"></div>

			<div v-if="listDetail.order_count" class="extra-menu-item" @click="goToOrderHistory()">View Order History</div>
			<div v-else class="extra-menu-item disabled">No Order History</div> 	
			
			<div v-if="listDetail.layaway_count" class="extra-menu-item" @click="goToLayawayHistory()" >View Layaway History</div>
			<div v-else class="extra-menu-item disabled">No Layaway History</div>

			<div class="extra-menu-item">
				<router-link v-if="listDetail.is_member" :to="'/range-memberships/members/' + listDetail.is_member" class="extra-menu-item">Manage Membership</router-link>
				<router-link v-else :to="{ path: '/range-memberships/members', query: { new_member_from_contact_id: listDetail.contact_id } }" class="extra-menu-item">Create Membership</router-link>
			</div>

			<div v-if="listDetail.inactive" class="extra-menu-item" @click="saveDeleteItem()">Reactive Customer</div>
			<div v-else class="extra-menu-item" @click="confirmDeactivateContact = true">Deactivate Customer</div>
		</div>

	</div>

	<loading v-if="loading" class="loading"></loading>

	<div v-else-if="showDetail" class="detail">

		<!--Contact Detail Tab-->
		<div v-if="showDetailTab == 'contact'" class="detail">
			<contact-detail-form :clientId="listDetail.client_id" :contact="listDetail" :hideControls="true" @update="setListDetail" />
		</div>

		<!--Addresses Tab-->
		<div v-if="showDetailTab == 'addresses'" class="detail">
			<div class="three-columns align-items-start" style="grid-column-gap: 3em;">
				<div v-for="(address, inded) in listDetail.addresses">
					<label-required :condition="address.address_type_id" label="Address Type"/>
					<select v-model="address.address_type_id">
						<option value="">Select...</option>
						<option v-for="addressType in getAddressTypes" :value="addressType.address_type_id">{{ addressType.description }}</option>
					</select>
					<label-required :condition="address.full_name" label="Full Name"/>
					<input type="text" v-model="address.full_name" />
					<label-required :condition="address.address_1" label="Address 1"/>
					<input type="text" v-model="address.address_1" />
					<label>Address 2</label>
					<input type="text" v-model="address.address_2" />
					<label-required :condition="address.city" label="City"/>
					<input type="text" v-model="address.city" />
					<div class="two-columns">
						<div>
							<label-required :condition="address.state" label="State"/>
							<state-select-input v-model="address.state" showProvinces="true" placeHolder="Select..." />
						</div>
						<div>
							<label-required :condition="address.zip_code" label="Zip Code"/>
							<input type="text" v-model="address.zip_code" />
						</div>
						<div>
							<label>Phone Number</label>
							<input type="text" v-model="address.phone_number" />
						</div>
						<div>
							<label>Email Address</label>
							<input type="text" v-model="address.email_address" />
						</div>
					</div>
					<div v-if="address.address_id">
						<input type="checkbox" v-model="address.inactive"> Inactive
					</div>
				</div>
			</div>
		</div>

		<!--Waivers and Attachments Detail Tab-->
		<div v-if="showDetailTab == 'attachments'" class="detail">
			<div class="section">
				<h2>Waivers / Applications</h2>
				<div v-if="waivers.length > 0">
					<div v-for="waiver in waivers" class="three-columns clickable-list-item" @click="showWaiver(waiver)">
						<div>
							<label>Waiver Date</label><br>
							{{ $formatDate(waiver.waiver_date) }}
						</div>
						<div>
							<label>Waiver Description</label><br>
							{{ waiver.description }}
						</div>
						<div>
							<label>Waiver Type</label><br>
							{{ waiver.waiver_type_code.capitalizeWords() }}
						</div>
					</div>
				</div>
				<div v-else class="empty-list" style="margin: 1em 0;">
					No waivers for this customer
				</div>
				<h2 style="margin: 1em 0 0 0;">Attachments</h2>				
				<div class="two-one-columns">
					<div class="border-right">
						<h3 class="attachment-title">Current attachments</h3>
						<div v-if="attachments.length > 0">
							<div v-for="(file, index) in attachments" class="one-two-columns" style="position: relative; margin: 0 1em 1em 1em; padding: 1em 0 0 0; border-top: 1px solid #ccc;">
								<div class="attachment-preview">
									<img v-if="file.file_preview_url" :src="file.file_preview_url">
									<div v-else>No Preview<br>Available</div>
								</div>
								<div class="attachment-details">
									<div class="detail-group">
										<label>File Name</label>
										<p>{{ file.file_name }}</p>
									</div>
									<div class="detail-group">
										<label>File Type</label>
										<p>{{ file.file_type }}</p>
									</div>
									<div class="detail-group">
										<label>Attached Date / Time</label>
										<p>{{ file.attached_time }}</p>
									</div>
									<button class="detail-group" @click="downloadContactAttachment( file.file_name, file.contact_attachment_id)" style="background-color: #4a4a4a;">Download</button>
								</div>
								<div class="button neutral inline" style="position: absolute; top: 0.5em; right: 0;" @click="confirmRemoveFile(file.contact_attachment_id)">
									<i class="fa fa-trash"></i>
								</div>
							</div>
						</div>
						<div v-else class="empty-list">
							No files are attached<br>to this customer
						</div>
					</div>
					<div>
						<h3 class="attachment-title">Add an attachment</h3>
						<div>
							<vue-dropzone @vdropzone-file-added="filesFileAdded" @vdropzone-success="filesUploadSuccess" @vdropzone-error="filesUploadError" ref="filesDropzone" id="site_background_dropzone" :options="filesDropzoneOptions"></vue-dropzone>
							<label><em>Allowed file types: pdf, jpg, png</em></label>
							<div class="dropzone dz-preview dz-file-preview uploaded-files" style="border: 0 none;"></div>
						</div>
					</div>
				</div>

				<div v-if="attachmentsMessage" class="message-overlay">
					<p v-html="attachmentsMessage"></p>
				</div>
			</div>
		</div>

		<!--User Detail Tab-->
		<div v-if="showDetailTab == 'user'" class="two-columns detail" style="grid-template-columns: 1fr 23em;">
			<div class="section">
				<h2>User Account</h2>
				<user-inputs :user="listDetail.user" />
			</div>
			<div class="section">
				<h2>Status</h2>
				This is your customer's current user account info.<br>
				<div class="note">A user account lets customers log into your website to manage their profile and orders.</div>
			</div>
		</div>

	</div>

	<div v-else class="list">

	    <div v-if="list.length == 0" class="empty-list">
	        This list is empty
	    </div>

	    <table v-else class="data-list" cellpadding="0" cellspacing="0">
			<thead>
				<tr>
					<th class="list">Customer</th>
					<th class="list">Phone / Email</th>
					<th class="list center">Member</th>
					<th class="list center">Payment</th>
					<th class="list center">User</th>
				</tr>
			</thead>
			<template v-for="(customer, index) in list">
			<tr v-if="getClient.contact_id != customer.contact_id" class="clickable-list-item" @click="getItem(customer)">
				<td class="list">
					{{ customer.full_name || customer.company_name }}<br>
					{{ customer.address_1 }} {{ customer.city }} {{ customer.state}} {{ customer.zip_code }}
				</td>
				<td class="list">
					{{ customer.phone_number }}<br>
					{{ customer.email_address }}
				</td>
				<td class="list center"><i v-if="customer.is_member" class='fa fa-check' /></td>
				<td class="list center"><i v-if="customer.subscription_id" class='fa fa-check' /></td>
				<td class="list center"><i v-if="customer.user_id" class='fa fa-check' /></td>
			</tr>
			<tr v-else><td class="list"><b>This record contains your store's client data and cannot be altered.</b></td></tr>
			</template>
		</table>

	</div>

    <message-modal v-if="message" :message="message" :loading="saving" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" @action="doMessageModalAction()" />

	<iframe id="download" src="" style="display: none;"></iframe>
	
	<waiver-detail v-if="showWaiverDetail.waiver_id" :showWaiverDetail="showWaiverDetail" @close="showWaiverDetail = {}" />

	<div v-if="confirmDeactivateContact" class="modal">
		<div class="inner">
			<h2>Deactivate Customer</h2>
			<div class="content">
				<p>This will prevent customer from appearing in search lists.</p>
				<p v-if="listDetail.order_count">This will <b>not</b> erase customer's order history.</p>
				<p v-if="listDetail.is_member">This <b>will</b> deactivate customer's <b>membership</b>{{ listDetail.subscription_id ? ' and stop processing their subscription' : '' }}.</p>
				<p>Are you sure you want to deactivate this customer?</p>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="confirmDeactivateContact = false">Cancel</div>
				<div class="button neutral spaced" @click="saveDeleteItem()">Yes, Deactivate</div>
			</div>
		</div>
	</div>

	<div v-if="showWaiverList" class="modal">
		<div class="inner">
			<h2>Waivers</h2>
			<div class="content">
				<div v-for="waiver in listDetail.waivers" class="three-columns clickable-list-item" @click="showWaiverList = false; showWaiverDetail = waiver;">
					<div>
						<label>Waiver Date</label><br>
						{{ $formatDate(waiver.waiver_date) }}
					</div>
					<div>
						<label>Waiver Description</label><br>
						{{ waiver.description }}
					</div>
					<div>
						<label>Waiver Type</label><br>
						{{ $caplitalizeWords(waiver.waiver_type_code) }}
					</div>
				</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showWaiverList = false">Cancel</div>
			</div>
		</div>
	</div>

</div>

</template>

<script>
import Loading from '@/components/Loading.vue'
import ContactDetailForm from '@/components/ContactDetailForm.vue'
import StateSelect from '@/components/StateSelect.vue'
import StateSelectInput from '@/components/StateSelectInput.vue'
import WaiverDetail from '@/components/WaiverDetail'
import UserInputs from '@/components/UserInputs.vue'
import LabelRequired from '@/components/LabelRequired.vue'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
export default {
	name: 'contacts',
	props: ['contact_id'],
    components: {
        'loading': Loading,
        'contact-detail-form': ContactDetailForm,
		'state-select': StateSelect,
		'state-select-input': StateSelectInput,
		'waiver-detail': WaiverDetail,
		'user-inputs': UserInputs,
		'label-required': LabelRequired,
		'vue-dropzone': vue2Dropzone
    },
	data() {
		return {
            loading: false,
			saving: false,
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			updateListAfterSave: false,
			showFilters: true,
			filters: {},
			filterMessage: '',
			filterLoading: false,
			addressTypes: [],
			list: [],
			paging: {},
			showDetail: false,
			showDetailTab: 'contact',
			listDetail: {},
			listIndex: '',
			gettingExport: false,
			exportMessage: '',
			waivers: [],
			showWaiverList: false,
			showWaiverDetail: {},
			attachments: [],
			showAttachments: false,
			attachmentsMessage: '',
			confirmDeactivateContact: false,
			removeFileId: '' 
		}
	},
	created() {
		if (this.getAddressTypes.length == 0) {
			this.apiCall({ destination: 'get_list_address_types' })
		}
		if (this.contact_id) {
			this.getItem(this.contact_id)
		} else {
			this.clearFilters()
		}
	},
	computed: {
		hasFilters() {
			return this.filters.search_term || this.filters.incomplete || this.filters.inactive || this.filters.state
		},
		canSave() {
			return true
		},
		filesDropzoneOptions() {
			return {
				url: this.getApiEndpoint + '?action=upload_contact_attachments',
                acceptedFiles: 'image/jpeg,image/png,application/pdf',
				withCredentials: true,
				uploadMultiple: false,
				paramName: 'upload_file',
				maxFilesize: 2.0,
				addRemoveLinks: false,
				thumbnailMethod: 'contain',
				dictDefaultMessage: 'Drop a file here, or click to find a file...',
				params: {
					fd_token: this.$getCookie('dashboard_token'),
					source: 'RANGE_MEMBERSHIPS',
					context: 'CONTACT_ATTACHMENTS',
					contact_id: this.listDetail.contact_id
				}
			}
		}
	},
	methods: {
		clearFilters() {
			this.filters = {
				page: 1,
				search_term: '',
				state: '',
				store: 0,
				web: 0,
				layaway: 0,
				orderless: 0,
				incomplete: 0,
				inactive: 0,
				do_export: 0
			}
			if (this.contact_id) {

			} else {
				this.getList()
			}
		},
		toggleFilter(which) {
			switch (which) {
				case "store":
					this.filters.web = 0
					this.filters.layaway = 0
					this.filters.orderless = 0
					this.filters.store = !this.filters.store
					break;
				case "layaway":
					this.filters.store = 0
					this.filters.web = 0
					this.filters.orderless = 0
					this.filters.layaway = !this.filters.layaway
					break;
				case "web":
					this.filters.store = 0
					this.filters.layaway = 0
					this.filters.orderless = 0
					this.filters.web = !this.filters.web
					break;
				case "orderless":
					this.filters.store = 0
					this.filters.layaway = 0
					this.filters.web = 0
					this.filters.orderless = !this.filters.orderless
					break;
				case "shopping_cart_type":
					break;
				default:
					this.filters[which] = !this.filters[which]
					break;
			}
			this.filters.page = 1
			this.getList()
		},
        filesUploadSuccess(file, response) {
            var obj = JSON.parse(response)
			if (obj.status != 'success') {
				this.attachmentsMessage = obj.message
			} else {
				let data = {
					contact_id: this.listDetail.contact_id,
					file_name: obj.file_name,
					file_type: obj.file_type
				}
				this.apiCall({ destination: 'save_contact_attachments', data: data }).then(obj => {
				    if (obj.status != 'success') {
				        this.attachmentsMessage = obj.message
						this.saving = false
				    } else {
						this.message = 'Attachment saved!'
						this.saving = false
						this.messageTimeout = 3000
				        this.getContactAttachments(this.listDetail.contact_id)
				    }
					this.$refs.filesDropzone.removeAllFiles()
				})
			}
        },
		goToOrderHistory() {
			if (this.listDetail.order_count == 0) { return }
			let returnRoute = this.$route.path
			if (returnRoute.indexOf(this.listDetail.contact_id) == -1) {
				returnRoute = returnRoute + '/' + this.listDetail.contact_id
			}
			sessionStorage.setItem("returnRoute", returnRoute);
			this.$router.push({ path: '/orders-sales/orders', query: { contact_id: this.listDetail.contact_id } })
		},
		goToLayawayHistory() {
			if (!this.listDetail.layaway_count) { return }

			let returnRoute = this.$route.path

			if (returnRoute.indexOf(this.listDetail.contact_id) == -1) {
				returnRoute = returnRoute + '/' + this.listDetail.contact_id
			}
			sessionStorage.setItem("returnRoute", returnRoute);
			this.$router.push({ path: '/orders-sales/layaways', query: { contact_id: this.listDetail.contact_id } })
		},
        filesUploadError(file, message, xhr) {
            this.attachmentsMessage = message
		},
        filesFileAdded(file) {
            console.log(file)
        },
		updateStateFilter(state) {
			this.filters.state = state
			this.getList()
		},
		getList(page) {
			if (this.loading) { return }
			this.showDetail = false;
			this.loading = true
			if (page) {
				this.filters.page = page
			}
			this.apiCall({ destination: 'get_list_contacts', data: this.filters }).then(obj => {
				this.loading = false
				if (this.filters.do_export) {
					this.filters.do_export = 0
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
						this.exportMessage = ''
					}, 1000)
				} else {
					if (obj.status == 'success') {
						this.list = obj.list
						if (obj.paging) {
							this.paging = obj.paging
						}
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		fetchDetailTab(detailTab, forceReload = false) {
			// if (!this.contact_membership_id || this.contact_membership_id == 'new') { return }
			if (detailTab == this.showDetailTab) { return }
			this.showDetailTab = detailTab
		},
		getItem(item) {
			let contactId = ''
			if (typeof item == 'object') {
				contactId = item.contact_id
			} else {
				contactId = item
			}
			if (this.loading) { return }
			this.loading = true
			this.updateListAfterSave = false
			for (let i = 0; i < this.list.length; i++) {
				if (this.list[i].contact_id == contactId) {
					this.listIndex = i
					break
				}
			}
			this.apiCall({ destination: 'get_item_contact', data: { contact_id: contactId } }).then(obj => {
				this.loading = false
				this.message = obj.message
				if (obj.status == 'success') {
					this.listDetail = obj.contact
					// add a blank address to the addresses array
					this.listDetail.addresses.push({
						address_type_id: '',
						full_name: '',
						address_1: '',
						address_2: '',
						city: '',
						state: '',
						zip_code: '',
						phone_number: '',
						email_address: ''
					})
					this.showDetail = true;
					this.getContactAttachments(this.listDetail.contact_id)
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getContactAttachments(contactId) {
			this.apiCall({ destination: 'get_contact_attachments', data: { contact_id: contactId } }).then(obj => {
				if (obj.status == 'success') {
					this.attachments = obj.file_reference
					this.attachmentsMessage = ''
					this.waivers = obj.waivers
				}
			})
		},
		downloadContactAttachment(fileName, fileId) {
			this.apiCall({ destination: 'download_contact_attachment', data: { file_name: fileName, file_id: fileId } }).then(async obj => {
				if (obj.status === 'success' && obj.file_download_url) {
					const response = await fetch(obj.file_download_url)
					const blob = await response.blob()

					const link = document.createElement('a')
					const objectURL = URL.createObjectURL(blob)
					link.href = objectURL
					link.download = fileName

					document.body.appendChild(link)
					link.click()
					URL.revokeObjectURL(objectURL)
					document.body.removeChild(link)
					this.message = 'File downloaded' 
				} else {
					this.message = obj.message
					this.attachments = this.attachments.filter(attachment => fileId !== attachment.contact_attachment_id)
				}
			})
			.catch(error => {
				this.message = 'Error fetching download URL:' + error
			});
			this.message = ''
		},
		confirmRemoveFile(fileId) {
			this.messageTimeout = 0
			this.removeFileId = fileId
			this.messageStatus = 'delete'
			this.message = 'This will permanently delete the file. Are you sure you want to proceed?'
		},
		removeContactAttachment() {
			const fileId = this.removeFileId
			this.apiCall({ destination: 'remove_contact_attachment', data: { file_id: fileId } }).then(obj => {
				if (obj.status === 'success') {
					this.attachments = this.attachments.filter(attachment => fileId !== attachment.contact_attachment_id)
					this.message = 'Attachment removed' 
					this.messageTimeout = 3000
				} else {
					this.message = obj.message
					this.messageTimeout = 3000
				}
			})
			.catch(error => {
				this.message = 'Error removing file ' + error
				this.messageTimeout = 3000
			});
			this.messageStatus = ''
			this.removeFileId = ''
		},
		showWaiver(waiver) {
			if (waiver.contact_waiver_id) {
				this.showWaiverDetail = waiver
				this.showAttachments = false
			} else {
				this.apiCall({ destination: 'get_item_application_pdf', data: { contact_id: waiver.contact_id } }).then(obj => {
				    if (obj.status == 'success' && obj.pdf_url) {
						// open pdf_url in a new window
						window.open(obj.pdf_url, '_blank')
				    }
				})
			}
		},
		setListDetail(detail) {
			this.listDetail = detail
		},
		addItem() {
			this.listDetail = {
				description: '',
				notes: [{ note: '', priority: '' }],
				internal_use_only: 0,
				inactive: 0
			}
			this.showDetail = true
		},
		saveItem() {
			switch (this.showDetailTab) {
				case "contact":
					this.saveContactInfo()
					break;
				case "addresses":
					this.saveAddresses()
					break;
				case "attachments":
					break;
				case "user":
					this.saveUser()
					break;
			}
		},
		saveContactInfo() {
			if (this.saving || !this.canSave) { return }
			// validate contact info
			this.message = ''
			if (!this.listDetail.first_name && this.listDetail.last_name && !this.listDetail.company_name) {
				this.message = 'First and Last Name or Company Name are required.'
			} else if (!this.listDetail.address_1) {
				this.message = 'Address is required.'
			} else if (!this.listDetail.city) {
				this.message += 'City is required.'
			} else if (!this.listDetail.state) {
				this.message += 'State is required.'
			} else if (!this.listDetail.zip_code) {
				this.message += 'Zip Code is required.'
			}
			if (this.message) {
				this.message += "<br><br>Please correct the info and try again."
				this.messageStatus = 'invalid'
				return
			}
			this.saving = true
			this.message = 'Saving contact'
			this.messageStatus = ''
			this.messageTimeout = 0
			this.apiCall({ destination: 'save_item_contact', data: this.listDetail }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.message = 'Contact saved!'
					setTimeout(() => {
						this.message = ''
						this.updateListAfterSave = true
						this.getItem(this.listDetail.contact_id ?? obj.contact_id)
					}, 1500)
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveAddresses() {
			if (this.saving || !this.canSave) { return }
			// iterate the addresses array and validate each address
			this.message = ''
			this.messageStatus = ''
			this.messageTimeout = 0
			for (let i = 0; i < this.listDetail.addresses.length; i++) {
				let address = this.listDetail.addresses[i]
				if (address.address_type_id && address.full_name && address.address_1 && address.city && address.state && address.zip_code) {
					console.log("yay!")
				} else if (address.address_type_id || address.full_name || address.address_1 || address.city || address.state || address.zip_code) {
					this.messageStatus = 'invalid'
					this.message = 'Some required address fields are missing.'
					break
				}
			}
			if (this.message) {
				return
			}
			this.saving = true
			this.message = 'Saving addresses'
			this.messageStatus = ''
			this.messageTimeout = 0
			this.apiCall({ destination: 'save_item_contact_addresses', data: { contact_id: this.listDetail.contact_id, addresses: this.listDetail.addresses } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.message = 'Addresses saved!'
					this.messageTimeout = 1500
					this.updateListAfterSave = true
					this.getItem(this.listDetail.contact_id)
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveUser() {
			console.log(this.listDetail.user);
			if (this.saving || this.contact_membership_id == 'new') { return }
			// validate user info
			this.message = ''
			this.messageTimeout = 0
			if (!this.listDetail.user.user_name || this.listDetail.user.user_name.length < 6) {
				this.message = 'User name with at least six characters is required.'
			} else if (this.listDetail.user.new_password && this.listDetail.user.new_password.length < 8) {
				this.message = 'Password with at least eight characters is required.'
			} else if (!this.listDetail.user.user_type_id) {
				this.message += 'User type is required.'
			}
			if (this.message) {
				this.message += "<br><br>Please correct and try again."
				this.messageStatus = 'invalid'
				return
			}
			this.saving = true
			this.message = 'Saving user...'

			this.listDetail.user.contact_id = this.listDetail.contact_id

			this.apiCall({ destination: 'save_item_user', data: this.listDetail.user }).then(obj => {
				this.saving = false
				if (obj.user_id) {
					this.listDetail.user.user_id = obj.user_id
				}
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.message = 'User saved!'
					setTimeout(() => {
						this.message = ''
						this.getItem(this.contact_membership_id ?? this.listDetail.contact_id)
					}, 3000)
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		confirmDeleteItem() {
			this.message = 'Are you sure you want to deactivate this customer?'
			this.messageStatus = 'delete'
		},
		saveDeleteItem() {
			if (this.saving || !this.canSave) { return }
			this.confirmDeactivateContact = false
			this.saving = true
			if (this.listDetail.inactive) {
				this.message = 'Reactivating contact'
			} else {
				this.message = 'Deactivating contact'
			}
			this.messageStatus = ''
			this.messageTimeout = 0
			this.listDetail.inactive = !this.listDetail.inactive
			this.apiCall({ destination: 'save_item_contact_deleted', data: { contact_id: this.listDetail.contact_id, inactive: this.listDetail.inactive } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					if (this.listDetail.inactive) {
						this.message = 'Contact deactivated!'
					} else {
						this.message = 'Contact reactivated!'
					}
					this.messageTimeout = 3000
					this.updateListAfterSave = true
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		doMessageModalAction() {
			switch (this.messageStatus) {
				case 'delete':
					if (this.removeFileId) {
						this.removeContactAttachment() 
					} else {
						this.saveDeleteItem()	
					}
					break
			}
		},
		backToList() {
			if (this.list.length === 0 ) {
				this.getList()
			}
			this.listDetail = {}
			this.showDetail = false
			if (this.updateListAfterSave) {
				this.getList()
			}
		},
		doExport() {
			if (this.gettingExport) {
				return false
			}
			this.gettingExport = true
			this.exportMessage = 'Generating CSV for ' + this.paging.total_items + ' items...'
			this.filters.product_id = ''
			this.filters.do_export = true
			this.getList()
		}
	}
}
</script>
<style lang="scss" scoped>
	.attachment-title {
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 0.25rem;
	}
	.one-two-columns {
		margin-top: 0px; 
		.attachment-preview {
			display: flex;
			justify-content: center;
			align-items: center;
			img {
				width: clamp(12.5rem, 10vw, 18.5rem);
				height: auto;
				object-fit: contain;
				padding-bottom: 1rem;
			}
		}
		.attachment-details {
			display: flex;
			justify-content: center;
			align-items: flex-start;
			flex-direction: column;
			padding: 1rem;
		}
		&:last-child {
			border-bottom: 1px solid rgb(204, 204, 204);
    	}
	}
</style>